import styled from "styled-components"
import { BREAKPOINTS } from "../GlobalTheme"
import { zeroLineHeight, wrapWhenNarrow } from '../styles/mixins'
import Navigator from "../components/Navigator"
import { TableCellTypes } from "../enums/TableCellTypes"
import { ReactNode, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import InputWithIcon from "../components/InputWithIcon"
import WhiteSpace from "../components/Whitespace"
import PaginationNavigator from "./PaginationNavigator"
import Icon from "../Icon"
import normalizeString from "../utils/normalizeString"

interface ColumnType {
  label?: string,
  width?: string
}

interface CellType {
  link?: string,
  sortBy?: string | number,
  onClick?: () => unknown,
  value?:
  { type: TableCellTypes.Image, src: string, color?: string } |
  { type: TableCellTypes.Label, value: string } |
  { type: TableCellTypes.LabelDivided, value1: string, value2: string } |
  { type: TableCellTypes.Status, label: string | JSX.Element, themeColor?: string, hexColor?: string, labelBelow?: string | null } |
  { type: TableCellTypes.Icon, src: string, color?: string } |
  { type: TableCellTypes.OpenButton } |
  { type: TableCellTypes.LabelWithIcon, value: string, src: string, iconColor?: string } |
  { type: TableCellTypes.Text, value: string },
  custom?: ReactNode
}

const Container = styled.div<{ $height: string }>`
  width: 100%;
  /* height="fill" means "take whatever vertical space my parent flex column has left". Callers that
     pass a calc(90vh - Npx) instead have to guess how much chrome sits above the table, and that
     guess is wrong at some viewport heights - which cuts the pagination row off below the shell. */
  height: ${props => (props.$height === 'fill' ? 'auto' : props.$height)};
  ${props => props.$height === 'fill' && 'flex: 1 1 auto; min-height: 0;'}
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;
  overflow-y: scroll;

  /* Callers pass calc(90vh - Npx), where N encodes the DESKTOP chrome above the table. On a phone the
     chrome is different (toolbars wrap, so there is more of it) and iOS resolves vh against the largest
     viewport while ~90-110px is browser UI - so a fixed height is either a sliver or overflows.
     Size to content instead and cap it, which adapts to whatever ends up above. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    /* A caller passing height="auto" means "grow, let my own scroller handle it" (ErrorLogs), so
       capping it there would change the contract. Only cap the calc(90vh - N) callers. */
    max-height: ${props => (props.$height === 'auto' ? 'none' : 'calc(100dvh - 160px)')};
    overscroll-behavior: contain;
  }
`

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* Below this the declared px+% column widths cannot all be honoured, so let the table take
       its min-content width and pan inside Container (whose overflow-x resolves to auto). */
    width: max-content;
    min-width: 100%;

    td, th {
      padding: 4px 6px;
      vertical-align: middle;
      /* The declared column widths are inline percentages sized for a desktop viewport. Against a
         max-content table they under-size columns, which wraps single-value cells onto two lines and
         inflates every row. Let the columns size to their content and pan instead. */
      width: auto !important;
    }

    /* The cells place their text with line-height 0 plus a 16px top margin. On a phone the
       line-height has to be real (wrapped text would otherwise overlap itself), and 16px margins
       on top of real line boxes made every row ~100px tall against 49px on desktop. Center the
       cell contents and let the line boxes set the height instead. */
    td p, th p {
      margin-top: 0px;
      margin-bottom: 0px;
      line-height: 1.3;
      /* The table takes its min-content width here and pans, so a cell has no reason to break a
         charger id mid-token - and letting it collapses the column and adds a line to every row. */
      overflow-wrap: normal;
    }

    /* Two-line cells still need the pair visually separated. */
    td p + p {
      margin-top: 2px;
    }
  }
`

const InfoRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.gray_300};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: sticky;
    top: 0px;
    z-index: 1;
    background-color: ${props => props.theme.colors.white};

    /* The sort chevron is nudged down by an inline margin-top that lined it up with a label
       positioned by margins against a zero line-height. With real line boxes it has to be centred
       against the label instead, and the inline style is why this needs !important. */
    td > div {
      align-items: center;
    }

    td > div > svg {
      margin-top: 0px !important;
    }
  }
`

const InfoLabel = styled.p`
  font-weight: 600;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_500};
`

const Row = styled.tr`
  border-bottom: 1px dashed ${props => props.theme.colors.gray_300};
  position: relative;
`

// --- Cell Presets ---

// Label
const Label = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
`

// LabelDivided
const LabelDividedTop = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
`
const LabelDividedBottom = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
`

// Status
const Status = styled.p<{ $themeColor?: string, $hexColor?: string, $leaveSpaceForLableBelow?: boolean }>`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.white};
  border-radius: 40px;
  width: fit-content;
  min-width: 70px;
  padding-left: 8px;
  padding-right: 8px;
  white-space: nowrap;
  line-height: 22px;
  text-align: center;
  margin-top: ${props => props.$leaveSpaceForLableBelow ? '5px' : ''};
  background-color: ${props => props.$themeColor ? props.theme.colors[props.$themeColor] : props.$hexColor};
`
const LabelBelowStatus = styled.p`
  font-weight: 400;
  font-size: 11px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: -4px;
  width: 100px;
  margin-left: -15px;
  text-align: center;
  ${wrapWhenNarrow}
`

// OpenButton
const OpenButtonIconContainer = styled.div`
  width: 16px;
  height: 16px;
  padding: 7px;
  background-color: #EFF2F5;
  border-radius: 6px;
  margin-right: 24px;
  float: right;
`

// Text
const Text = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
`

export default function Table({ id, columns, data, height, isSearchEnabled, isSearchCtrlFEnabled, isPaginationEnabled, triggerRefresh, isRenderEmpty, isAutofocusOnSearchInput, onSearchTermChange, searchBarSlot, searchPlaceholder }: { id: string, columns: ColumnType[], data: CellType[][], height: string, isSearchEnabled?: boolean, isSearchCtrlFEnabled?: boolean, isPaginationEnabled?: boolean, triggerRefresh?: number, isRenderEmpty?: boolean, isAutofocusOnSearchInput?: boolean, onSearchTermChange?: (term: string) => void, searchBarSlot?: ReactNode, searchPlaceholder?: string }) {

  const { t } = useTranslation()

  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [showing, setShowing] = useState<CellType[][]>(data)
  const [sortedColumn, setSortedColumn] = useState<number>(0)
  const [isSortDescending, setIsSortDescending] = useState<boolean>()
  const [searchTerm, setSearchTerm] = useState<string>()
  const [page, setPage] = useState<number>(1)
  const [pageStep, setPageStep] = useState<number>(0)
  const rowRef = useRef<{value: CellType[], element: HTMLElement}[]>([])
  const [isSearchCtrlFActive, setIsSearchCtrlFActive] = useState<boolean>(isSearchCtrlFEnabled || false)
  const [ctrlFStyleSearchResults, setCtrlFStyleSearchResults] = useState<{value: CellType[], element: HTMLElement}[]>([])
  const [ctrlFStyleSearchCurrentIndex, setCtrlFStyleSearchCurrentIndex] = useState<number>(0)

  useEffect(() => {

    // Keep the parent in sync with the (reset) search term on mount / remount, so anything the parent
    // derives from it (e.g. filtered export) starts from a clean slate
    onSearchTermChange?.('')

    // Autofocus on search input if enabled
    if (isAutofocusOnSearchInput) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    // Try to load the sorting preference form localStorage
    const sortPreferenceString = localStorage.getItem('preferedTableSort')
    if (sortPreferenceString) {
      const sortPreference = JSON.parse(sortPreferenceString)
      if (sortPreference[id]) {
        showSorted(sortPreference[id].column, sortPreference[id].isDescending, data)
        return
      }
    }

    // If no sort preference is found, find the first column that has a sortBy key, and sort by that
    for (let i = 0; i < data[0].length; i++) {
      if (data[0][i].sortBy != undefined) {
        showSorted(i)
        break
      }
    }
  }, [])

  function updateSortPreferenceInLocalStorage(column: number, isDescending: boolean) {
    const currentPreferenceString = localStorage.getItem('preferedTableSort')
    let currentPreference: { [key: string | number]: unknown } = {}
    if (currentPreferenceString) {
      currentPreference = JSON.parse(currentPreferenceString)
    }
    currentPreference[id] = { column, isDescending }
    localStorage.setItem('preferedTableSort', JSON.stringify(currentPreference))
  }

  useEffect(() => {
    // This it for the parent component to be able to trigger a refresh when data changes
    handleSearch(searchTerm || '')
  }, [triggerRefresh])

  useEffect(() => {
    // Only run if there was an acutal input, dont run on first render
    if (searchTerm != undefined) {
      // Let the parent mirror the current search term (e.g. to export only matching rows)
      onSearchTermChange?.(searchTerm)
      // Handling the search bar input
      if (searchTerm != '') {
        handleSearch(searchTerm)
      } else {
        // Clearing the search must also turn the ctrlF "filter / hide non-matching" mode back off,
        // otherwise every row stays hidden (display: none) and the toggle button is gone since it
        // only renders while a search term is present, leaving the user stuck with an empty table
        setIsSearchCtrlFActive(isSearchCtrlFEnabled || false)
        setCtrlFStyleSearchResults([])
        showSorted(sortedColumn, isSortDescending, data)
        containerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
      }
    }
  }, [searchTerm])

  function handleSearch(term: string) {
    // Handle search and load it into showing state
    if (isSearchCtrlFEnabled) {
      const result = []
      for (const row of rowRef.current) {
        for (const cell of row.value) {
          if (normalizeString(JSON.stringify({ ...cell, custom: '' })).includes(normalizeString(term))) {
            result.push(row)
            break
          }
        }
      }
      setCtrlFStyleSearchResults(result)
      if (result.length > 0) {
        setCtrlFStyleSearchCurrentIndex(0)
        // Only scroll to the first result if an actual search is in progress
        if (searchTerm && searchTerm != '') {
          result[0].element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
        }
      } else {
        setCtrlFStyleSearchCurrentIndex(-1)
        containerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
      }

    } else {
      const result = []
      for (const row of data) {
        for (const cell of row) {
          try {
            if (normalizeString(JSON.stringify({ ...cell, custom: '' })).includes(normalizeString(term))) {
              result.push(row)
              break
            }
          } catch (err) {
            // Probably a circular json error because of a component in the json, most likely can safely skip it
          }
        }
      }
      showSorted(sortedColumn, isSortDescending, result)
    }
  }

  function handleCtrlFStyleSearchStep(direction: 'forward' | 'backward') {
    // Step up or down in ctrlF style search
    if (ctrlFStyleSearchResults.length > 0) {
      if (direction == 'forward') {
        if (ctrlFStyleSearchCurrentIndex < ctrlFStyleSearchResults.length - 1) {
          ctrlFStyleSearchResults[ctrlFStyleSearchCurrentIndex + 1].element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
          setCtrlFStyleSearchCurrentIndex(ctrlFStyleSearchCurrentIndex + 1)
        }
      } else {
        if (ctrlFStyleSearchCurrentIndex > 0) {
          ctrlFStyleSearchResults[ctrlFStyleSearchCurrentIndex - 1].element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
          setCtrlFStyleSearchCurrentIndex(ctrlFStyleSearchCurrentIndex - 1)
        }
      }
    }
  }

  function showSorted(column: number, isDescending?: boolean, dataToSort?: CellType[][]) {
    if (data[0][column] && data[0][column].sortBy != undefined) {

      // Is there is no data passed to show, just use the currently shown
      if (!dataToSort) {
        dataToSort = [...showing]
      } else {
        dataToSort = [...dataToSort]
      }

      // If isDescending is not passed and we are sorting the same column, than we only need to reverse the current direction
      if (isDescending == undefined) {
        if (column == sortedColumn) {
          isDescending = !isSortDescending
        }
      }

      dataToSort.sort((a, b) => (a[column].sortBy || '') < (b[column].sortBy || '') ? -1 : 1)

      if (isDescending) {
        dataToSort.reverse()
        setIsSortDescending(true)
      } else {
        setIsSortDescending(false)
      }

      setShowing(dataToSort)
      updateSortPreferenceInLocalStorage(column, isDescending || false)
      setSortedColumn(column)
    }
  }

  return (
    <>
      {isSearchEnabled && <>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: '8px', minWidth: 0 }}>
          <div style={{ minWidth: 0 }} onKeyDown={(e) => { if (e.key === 'Enter') handleCtrlFStyleSearchStep(e.shiftKey ? 'backward' : 'forward') }}>
            <InputWithIcon onInput={(input) => setSearchTerm(input)} ref={searchInputRef} width="max(12vw, 154px)" placeholder={searchPlaceholder ? t(searchPlaceholder) : t('chargers_search')} icon="general.search" iconColor="gray_600" />
          </div>
          {isSearchCtrlFEnabled && searchTerm && searchTerm.length > 1 && <>
            <Icon path="general.filter" width="18px" height="18px" marginleft="24px" margintop="10px" color={isSearchCtrlFActive ? 'gray_600' : 'primary'} onClick={() => {
              if (!isSearchCtrlFActive) {
                setTimeout(() => {
                  ctrlFStyleSearchResults[ctrlFStyleSearchCurrentIndex].element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
                }, 300)
              }
              setIsSearchCtrlFActive(!isSearchCtrlFActive)
            }} />
          </>}
          {isSearchCtrlFActive && searchTerm && searchTerm.length > 1 && <>
            <div style={{ display: 'flex' }}>
              <WhiteSpace width="18px" />
              <Icon path="general.backArrow" color="gray_800" width="16px" height="16px" margintop="12px" onClick={() => handleCtrlFStyleSearchStep('backward')} />
              <WhiteSpace width="12px" />
              <p style={{ marginTop: '12px', fontSize: '12px', color: 'gray' }} >{`${ctrlFStyleSearchCurrentIndex + 1} / ${ctrlFStyleSearchResults.length}`}</p>
              <WhiteSpace width="12px" />
              <Icon path="general.forwardArrow" color="gray_800" width="16px" height="16px" margintop="12px" onClick={() => handleCtrlFStyleSearchStep('forward')} />
            </div>
          </>}
          {searchBarSlot && <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>{searchBarSlot}</div>}
        </div>
      </>}
      <WhiteSpace height="12px" />
      <Container $height={height} ref={containerRef}>
        <TableElement key={triggerRefresh}>
          <tbody>
            <InfoRow>
              {columns.map((column, columnIndex) => (
                <td key={`column${columnIndex}`} style={column.width ? { width: column.width } : {}}>
                  {column.label && (
                    <div style={{ display: 'flex', cursor: 'pointer' }} onClick={() => showSorted(columnIndex)}>
                      <InfoLabel>{column.label}</InfoLabel>
                      {data[0][columnIndex].sortBy != undefined && (
                        <Icon path={columnIndex != sortedColumn ? "general.downArrow" : (isSortDescending || false) ? "general.upArrow" : "general.downArrow"} color={sortedColumn == columnIndex ? "primary" : "black"} width="12px" height="12px" margintop="10px" marginleft="6px" />
                      )}
                    </div>
                  )}
                </td>
              ))}
            </InfoRow>
            {!isRenderEmpty && showing.map((row, rowIndex) => (isPaginationEnabled ? rowIndex + 1 > (page - 1) * pageStep && rowIndex < page * pageStep : true) && (
              <Row key={`row${rowIndex}`} ref={rowElement => rowRef.current[rowIndex] = {value: row, element: rowElement as HTMLElement}} style={isSearchCtrlFEnabled && ctrlFStyleSearchResults.some(obj => obj.value === row) && searchTerm && searchTerm.length > 1 ? isSearchCtrlFActive ? { backgroundColor: '#fffad1' } : {} : isSearchCtrlFEnabled ? isSearchCtrlFActive ? {} : { display: "none" } : {}}>
                {row.map((cell, cellIndex) => (
                  <td key={`cell${rowIndex}-${cellIndex}`} onClick={cell.onClick} style={cell.onClick ? { cursor: 'pointer' } : {}}>
                    <Navigator to={cell.link || ''}>
                      {cell.value && (
                        <>
                          {cell.value.type == TableCellTypes.Image && <>
                            <Icon path={cell.value.src} width="30px" height="30px" marginleft="9px" marginright="16px" margintop="2px" color={cell.value.color} />
                          </>}

                          {cell.value.type == TableCellTypes.Label && <>
                            <Label>{cell.value.value}</Label>
                          </>}

                          {cell.value.type == TableCellTypes.LabelDivided && <>
                            <LabelDividedTop>{cell.value.value1}</LabelDividedTop>
                            <LabelDividedBottom>{cell.value.value2}</LabelDividedBottom>
                          </>}

                          {cell.value.type == TableCellTypes.Status && <>
                            {cell.value.labelBelow ? <>
                              <Status $themeColor={cell.value.themeColor} $hexColor={cell.value.hexColor} $leaveSpaceForLableBelow>{cell.value.label}</Status>
                              <LabelBelowStatus>{cell.value.labelBelow}</LabelBelowStatus>
                            </> : <>
                              <Status $themeColor={cell.value.themeColor} $hexColor={cell.value.hexColor}>{cell.value.label}</Status>
                            </>}
                          </>}

                          {cell.value.type == TableCellTypes.Icon && <>
                            <Icon path={cell.value.src} color={cell.value.color} width="10px" height="10px" marginleft="9px" marginright="16px" />
                          </>}

                          {cell.value.type == TableCellTypes.OpenButton && <>
                            <OpenButtonIconContainer>
                              <Icon path="general.forwardArrow" width="100%" height="100%" />
                            </OpenButtonIconContainer>
                          </>}

                          {cell.value.type == TableCellTypes.LabelWithIcon && <>
                            <div style={{ display: 'flex' }}>
                              <Icon path={cell.value.src} width="20px" height="20px" marginright="4px" margintop="6px" color={cell.value.iconColor} />
                              <Label>{cell.value.value}</Label>
                            </div>
                          </>}

                          {cell.value.type == TableCellTypes.Text && <>
                            <Text>{cell.value.value}</Text>
                          </>}
                        </>
                      )}
                      {!cell.value && cell.custom && cell.custom}
                    </Navigator>
                  </td>
                ))}
              </Row>
            ))}
          </tbody>
        </TableElement>
      </Container>
      {showing && isPaginationEnabled && <>
        <WhiteSpace height="16px" />
        <PaginationNavigator notAbsolute width="100%" numberOfEntries={showing.length} page={page} setPage={setPage} step={pageStep} setStep={setPageStep} />
      </>}
    </>
  )
}